/* eslint-disable */
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { Button, Col, ConfigProvider, Form, Row, Input } from "antd";
import { InboxOutlined, FilterOutlined, SearchOutlined, ArrowRightOutlined, TeamOutlined } from "@ant-design/icons";
import "./ric.css";
import * as React from "react";
import { useStore } from "../stores/root-store-context";
import "../tmp.css";
import Headers from '../../../../../../redux/Actions/Headers/Header'
import ResponsiveGrid from '../../../../../../Components/TableMui/ResponsiveGrid';
import Card from '../../../../../../Components/Card/Card';
import { PaginationConfig } from "antd/es/pagination";
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import { formatPhoneForDisplay } from "../../../../../../utils/phoneDisplayFormat";

interface SearchData {
  searchText?: string;
  status?: boolean;
}

const { Search } = Input;

const STORAGE_KEY = "ric_list_state";

interface ListState {
  page: number;
  pageSize: number;
  field: string | null;
  sortOrder: string | null;
}

const DEFAULT_STATE: ListState = {
  page: 1,
  pageSize: 25,
  field: "contactFullName",
  sortOrder: "ASC",
};

const getSavedState = (): ListState => {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) return { ...DEFAULT_STATE, ...JSON.parse(saved) };
  } catch {}
  return DEFAULT_STATE;
};

const saveState = (state: Partial<ListState>) => {
  try {
    const current = getSavedState();
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...current, ...state }));
  } catch {}
};

const clearState = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {}
};

const RIC = observer(() => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = useSelector((state: any) => !!state.Headers?.isHeaderStacked);
  const { ricStore, applicationStore } = useStore();
  const lastRequestId = React.useRef(0);

  const [searchData, setSearchData] = React.useState<SearchData>({
    searchText: applicationStore?.searchValue ?? "",
    status: applicationStore?.statusValue ?? false,
  });

  const [loading, setIsLoading] = React.useState(false);

  const [pagination, setPagination] = React.useState<PaginationConfig>(() => {
    const saved = getSavedState();
    return {
      current: saved.page,
      pageSize: saved.pageSize,
      total: 0,
    };
  });

  // Controlled sort (MUI format). Initialized from the saved state.
  const [sortModel, setSortModel] = React.useState<any[]>(() => {
    const saved = getSavedState();
    return saved.field
      ? [{ field: saved.field, sort: saved.sortOrder === "DESC" ? "desc" : "asc" }]
      : [];
  });

  const searchDataRef = React.useRef(searchData);
  useEffect(() => { searchDataRef.current = searchData; }, [searchData]);

  // Debounce timer for the auto-search-as-you-type behavior on the search bar.
  const searchDebounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Minimum characters before the search bar auto-fires. Typing fewer than this
  // (but more than zero) keeps the previous results; clearing the field resets.
  const MIN_SEARCH_CHARS = 3;

  // Runs a fresh search from the search bar: resets to page 1 and reuses the
  // current sort/status. Shared by both the auto-search and the lupa/Enter.
  const triggerSearch = (value: string) => {
    applicationStore.setSearchValue(value);

    const { field, sortOrder } = getSavedState();
    saveState({ page: 1, pageSize: DEFAULT_STATE.pageSize });
    setPagination({ current: 1, pageSize: DEFAULT_STATE.pageSize, total: 0 });

    OnSearch(field, sortOrder, value, searchDataRef.current?.status, DEFAULT_STATE.pageSize, 1);
  };

  // Clear the pending debounce when the component unmounts.
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  const prevPropertyIdRef = React.useRef<number | undefined>(undefined);

  useEffect(() => {
    const currentId = applicationStore.SelectedProperties?.id;
    if (currentId) {
      const isPropertyChange =
        prevPropertyIdRef.current !== undefined &&
        prevPropertyIdRef.current !== currentId;
      prevPropertyIdRef.current = currentId;

      let status = searchDataRef.current?.status ?? false;
      let searchText = searchDataRef.current?.searchText ?? "";
      if (isPropertyChange) {
        status = false;
        searchText = "";
        setSearchData((prev) => ({ ...prev, status: false, searchText: searchText }));
        applicationStore.setStatusValue(false);
        applicationStore.setSearchValue("");
      }

      const load = async () => {
        await ricStore.getFilterList(currentId);
      };
      load().catch(console.log);

      const { page, pageSize, field, sortOrder } = getSavedState();
      OnSearch(field, sortOrder, searchText, status, pageSize, page);
    }
  }, [applicationStore.SelectedProperties]);

  useEffect(() => {
    ricStore.setAccount(undefined);
  }, []);

  // Association Name only shows when no specific association is selected
  // (nothing selected or "All").
  const selectedId = applicationStore.SelectedProperties?.id;
  const showAssociation = selectedId == null || selectedId === 0;

  // Columns kept as in staging (titles, order and widths), just converted
  // to the ResponsiveGrid format.
  const columns = [
    {
      field: "community",
      label: "Association\nName",
      flex: 1.2,
      minWidth: 110,
      visible: showAssociation,
    },
    {
      field: "accountType",
      label: "Account\nType",
      flex: 0.55,
      minWidth: 85,
    },
    {
      field: "account",
      label: "Account #",
      flex: 0.5,
      minWidth: 85,
    },
    {
      field: "address",
      label: "Unit\nAddress",
      flex: 1.7,
      minWidth: 140,
    },
    {
      field: "residentContactKey",
      label: "Contact\nID",
      flex: 0.5,
      minWidth: 85,
    },
    {
      field: "contactFullName",
      label: "Contact \nFull Name",
      flex: 1.6,
      minWidth: 160,
      primary: true,
      render: (_, record) => {
        const full = record.contactFullName || "";
        return (
          <span className="rg-cell-ellipsis" title={full} style={{ fontWeight: 600 }}>
            {full}
          </span>
        );
      },
    },
    {
      field: "email",
      label: "Email",
      flex: 1.8,
      minWidth: 170,
    },
    {
      field: "phone",
      label: "Phone",
      flex: 1,
      minWidth: 210,
      render: (_, record) => {
        if (!record.phone && !record.secondPhone) {
          return <span className="rg-dash">—</span>;
        }
        return (
          <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.25 }}>
            {record.phone && <span style={{ whiteSpace: "nowrap" }}><b>{(record.phoneType || "Home").trim()}:</b> {formatPhoneForDisplay(record.phone)}</span>}
            {record.secondPhone && <span style={{ whiteSpace: "nowrap" }}><b>{(record.secondPhoneType || "Mobile").trim()}:</b> {formatPhoneForDisplay(record.secondPhone)}</span>}
          </div>
        );
      },
    },
    {
      field: "actions",
      label: "Actions",
      width: 92,
      sortable: false,
      render: (_, record) => (
        <Button
          className="ric-view-btn ric__view-btn"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/AAM/Ric/Account/${record?.account}`);
          }}
        >
          View <ArrowRightOutlined />
        </Button>
      ),
    },
  ];

  // Current sort (backend format), derived from the controlled sortModel
  const currentSort = () => {
    const first = sortModel[0];
    return {
      field: first?.field ?? null,
      sortOrder: first ? (first.sort === "asc" ? "ASC" : "DESC") : null,
    };
  };

  // Header click (server-side). Keeps the current page, as before.
  const handleSortChange = (model: any[]) => {
    setSortModel(model);
    const first = model[0];
    const field: string | null = first?.field ?? null;
    const sortOrder: string | null = first ? (first.sort === "asc" ? "ASC" : "DESC") : null;

    OnSearch(
      field,
      sortOrder,
      searchData?.searchText || "",
      searchData?.status,
      pagination.pageSize || DEFAULT_STATE.pageSize,
      pagination.current || 1
    );
  };

  // Page / page-size change (server-side)
  const handlePageChange = (current: number, pageSize: number) => {
    setPagination((prev) => ({ ...prev, current, pageSize }));
    const { field, sortOrder } = currentSort();

    OnSearch(
      field,
      sortOrder,
      searchData?.searchText || "",
      searchData?.status,
      pageSize,
      current
    );
  };

  // Row click: navigate only for active residents
  const handleRowClick = (row: any) => {
    if (!row?.status) return;
    navigate(`/AAM/Ric/Account/${row?.account}`);
  };

  const OnSearch = async (
    field: string | null,
    sortOrder: string | null,
    value: string,
    status: boolean,
    pageSize: number,
    pageNumber: number
  ) => {
    const requestId = ++lastRequestId.current;

    // Skip the backend call when no association is selected, or when it is
    // "All" without search text. Just clear the list.
    const selectedId = applicationStore.SelectedProperties?.id;
    const noAssociation = selectedId == null;
    const allWithoutSearch = selectedId === 0 && !(value && value.trim());
    if (noAssociation || allWithoutSearch) {
      ricStore.setAccountList([]);
      ricStore.setAccountListCount(0);
      setPagination((prev) => ({ ...prev, total: 0 }));
      setIsLoading(false);
      return;
    }

    const req = {
      searchText: value ?? "",
      n_propertyID:
        applicationStore.SelectedProperties?.id === 0
          ? null
          : applicationStore.SelectedProperties?.id,
      includePreOwners: false,
      pageNumber,
      rowsPerPage: pageSize,
      status: !status,
      sort: field
        ? field.charAt(0).toUpperCase() + field.slice(1)
        : null,
      sortDirection: sortOrder || null,
    };

    setIsLoading(true);

    try {
      const res = await ricStore.searchInformation(req);

      if (requestId !== lastRequestId.current) return;

      ricStore.setAccountList(res?.data);

      const total = res?.data?.length > 0 ? res?.data[0]?.totalCount : 0;
      ricStore.setAccountListCount(total);

      setPagination((prev) => ({ ...prev, current: pageNumber, pageSize, total }));

      saveState({ page: pageNumber, pageSize, field: field ?? DEFAULT_STATE.field, sortOrder: sortOrder ?? DEFAULT_STATE.sortOrder,});
    } catch (error) {
      console.error(error);
    } finally {
      if (requestId === lastRequestId.current) {
        setIsLoading(false);
      }
    }
  };

  const handlePropertyChange = () => {
    clearState();
    OnSearch(
      DEFAULT_STATE.field,
      DEFAULT_STATE.sortOrder,
      searchDataRef.current?.searchText,
      searchDataRef.current?.status,
      DEFAULT_STATE.pageSize,
      1
    );
  };

  useEffect(() => {
    dispatch(
      Headers.setHeader({
        title: "RIC - Resident Information Console",
        subtitle: "Search and manage resident information across your communities.",
        icon: <TeamOutlined />,
        actions: [{ type: "property", onClick: handlePropertyChange }],
      })
    );

    return () => {
      dispatch(Headers.resetHeader());
    };
  }, [dispatch]);

  // Empty-state message + icon based on the selection/filter state
  const hasSearchText = !!(searchData?.searchText && searchData.searchText.trim());
  let emptyMessage: string;
  let emptyIcon: React.ReactNode;
  if (selectedId == null) {
    emptyMessage = isMobile
      ? 'Please select an Association or "All" above.'
      : 'Please select an Association or "All" in the top right corner.';
    emptyIcon = <FilterOutlined />;
  } else if (selectedId === 0 && !hasSearchText) {
    emptyMessage = "Please enter search criteria in the search bar above.";
    emptyIcon = <SearchOutlined />;
  } else {
    emptyMessage = "No data.";
    emptyIcon = <InboxOutlined />;
  }

  return (
    <div className="ric">
      <ConfigProvider
        key="RicConfigProvider"
        theme={{
          token: {},
          components: {
            Tabs: {
              cardBg: "#5a7890",
              colorBgContainer: "#dedede",
              itemColor: "#ffffff",
              itemSelectedColor: "#000000",
              itemHoverColor: "#000000",
              itemActiveColor: "#000000",
            },
            Button: {
              primaryColor: "#ffffff",
              defaultColor: "#ffffff",
              colorBgContainer: "#5a7890",
              controlHeight: 38,
              borderRadius: 10,

              defaultHoverBg: "#dedede",
              defaultHoverColor: "#000000",
              defaultBorderColor: "#dedede",
              defaultActiveColor: "#ffffff",
              defaultHoverBorderColor: "#5a7890",
            },
            Table: {
              rowHoverBg: "rgb(218, 221, 225)",
            },
          },
        }}
      >
        <Card className="ric__card">
          <div className="ric__toolbar">
            <Form form={form} layout="vertical" className="ric__search-form">
              <Search
                placeholder="Search residents by name, email, address..."
                allowClear
                prefix={<SearchOutlined className="ric__search-icon" />}
                value={searchData.searchText}
                onChange={(e) => {
                  const value = e.target.value;

                  setSearchData((prev) => ({
                    ...prev,
                    searchText: value,
                  }));

                  // Auto-search as the user types (debounced), always using the
                  // latest value. Fires from MIN_SEARCH_CHARS onward; clearing
                  // the field (length 0) re-runs to reset the list.
                  if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);

                  const trimmed = value.trim();
                  if (trimmed.length >= MIN_SEARCH_CHARS || trimmed.length === 0) {
                    searchDebounceRef.current = setTimeout(() => {
                      triggerSearch(value);
                    }, 400);
                  }
                }}
                onSearch={(value) => {
                  // Immediate search on lupa click / Enter: cancel any pending
                  // debounce so we don't fire twice.
                  if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);

                  triggerSearch(value);
                }}
              />
            </Form>
          </div>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <ResponsiveGrid
                columns={columns}
                rows={ricStore.accountList ?? []}
                getRowId={(row) => row.residentContactKey}
                onRowClick={handleRowClick}
                loading={loading || applicationStore.isLoading}
                emptyText={emptyMessage}
                emptyIcon={emptyIcon}
                server={{
                  page: pagination.current || 1,
                  pageSize: pagination.pageSize || DEFAULT_STATE.pageSize,
                  total: pagination.total || 0,
                  sortModel,
                  onSortChange: handleSortChange,
                  onChange: handlePageChange,
                }}
              />
            </Col>
          </Row>
        </Card>
      </ConfigProvider>
    </div>
  );
});

export default RIC;